"use client";

import { Button } from "@/components/atoms/Button";
import CurvedRightArrow from "@/assets/CurvedRightArrow";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import {
  handleOpenLeaddrop,
  setEnrollmentPageBinding,
} from "@/store/slices/leaddrop";
import type { RootState } from "@/store/store";
import { ArrowRight } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  LEAD_DROP_POPUP_VARIANTS,
  LEAD_DROP_VARIANTS,
} from "../../../constants";
import Leaddrop7 from "@/widgets/leaddrop/leaddrop7/Leaddrop7";
import { useLeaddrop7WidgetState } from "@/widgets/leaddrop/leaddrop7/useLeaddrop7WidgetState";
import type { ILeadDropPopup } from "@/api/admin/leadDropPopup";
import type { ICourse } from "@/api/admin/course";
import InlineLeadForm from "../leaddropform/InlineLeadForm";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";

export interface Enrollments2Data {
  id?: string;
  status?: boolean;
  headline_primary?: string;
  headline_accent?: string;
  body_paragraph_1?: string;
  body_paragraph_2?: string;
  body_paragraph_3?: string;
  stat_1_value?: string;
  stat_1_label?: string;
  stat_2_value?: string;
  stat_2_label?: string;
  enable_lead_popup?: boolean;
  /** Desktop slot renders the page's resolved inline lead form instead of the Apply CTA. */
  enable_lead_form?: boolean;
  bound_course_id?: number | string | null;
}

interface Enrollments2Props {
  data: Enrollments2Data | { data: Enrollments2Data };
}

function resolvePayload(
  data: Enrollments2Props["data"],
): Enrollments2Data | undefined {
  const anyData = data as { data?: Enrollments2Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Enrollments2Data;
  }
  return data as Enrollments2Data;
}

const DEFAULT_P1 =
  "This production-first, AI-native engineering programme delivered by IIT Kharagpur covers frontend, backend, retrieval systems, agentic workflows, and production deployment.";
const DEFAULT_P2 =
  "IIT Kharagpur is a global leader in engineering education and AI research, with a legacy of building world-class engineers.";
const DEFAULT_P3 =
  "You will build an end-to-end AI system—from architecture and data pipelines to deployment and monitoring—so you can ship real systems, not just prototypes.";

const SECTION_BG = "#FFFFFF";
const BODY_TEXT = "#111827";
const HEADLINE_ACCENT = "#DB5803";
/** IIT lead panel card: matches reference `bg-secondary-light-200` / rgb(229,229,249) */
/** Layout (`flex` / `hidden min-[1024px]:flex`) applied at each call site so `hidden` is not overridden by merge. */
const enrollmentLeadCardClass = cn(
  "flex-col items-start overflow-hidden",
  "pt-[40px] pb-[26px] px-[39px]",
  "min-h-[500px] w-full max-w-[392px]",
  "bg-[#E5E5F9] rounded-tr-[42px] rounded-bl-[42px]",
  // `lg:` is remapped to 1430px in this codebase; gate the desktop layout on a
  // literal 1024px so the embedded form (which itself uses min-[1024px]:) shows.
  "min-[1024px]:w-[392px] min-[1024px]:max-w-[392px] min-[1024px]:shrink-0",
);
const CTA_CLASS =
  cn(
    "!bg-[#DB5803] !text-white hover:!bg-[#DB5803] hover:opacity-95",
    "shadow-md transition-[box-shadow,opacity,transform] active:scale-[0.98]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#DB5803] focus-visible:ring-offset-2",
  );

const applyNowArrowIcon = (
  <ArrowRight
    className="size-5 shrink-0 text-white"
    strokeWidth={2.25}
    aria-hidden
  />
);

/** Diagonal up-right arrow matching the reference `size="lg"` mobile CTA. */
const applyNowDiagonalArrow = (
  <CurvedRightArrow className="size-5 shrink-0" fill="currentColor" />
);

/**
 * Mobile Apply Now CTA — full-width primary button mirroring the reference
 * `<Button variant="default" size="lg" className="w-full" />`:
 * h-10 / rounded-md / px-8 / text-xs font-medium, orange bg, pure white text.
 * (`text-white` resolves to #f8f9fa in this theme, so #FFFFFF is forced.)
 */
const MOBILE_CTA_CLASS = cn(
  "w-full h-10 rounded-md px-8",
  "text-center text-xs font-medium tracking-normal",
  "!bg-[#DB5803] !text-[#FFFFFF] hover:opacity-95",
  "transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#DB5803]",
);

function safeTrim(s: string | null | undefined): string {
  return (s ?? "").trim();
}

/**
 * Mobile lead CTA — opens the site lead popup.
 *
 * Module level, and shared by every branch below: below 1024px this widget always
 * offers the button, never an embedded form. The inline-form option replaces the
 * DESKTOP card only, so mobile capture is unchanged whichever way the toggle is set.
 */
const MobileApplyCta = ({ onClick }: { onClick: () => void }) => (
  <div className="flex w-full min-[1024px]:hidden">
    <Button
      type="button"
      classType="ghost"
      className={MOBILE_CTA_CLASS}
      withArrow
      arrowIcon={applyNowDiagonalArrow}
      onClick={onClick}
      aria-haspopup="dialog"
    >
      Apply Now
    </Button>
  </div>
);

const Enrollments2 = ({ data }: Enrollments2Props) => {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);
  const dispatch = useDispatch();
  const {
    formData,
    courses,
    defaultValues,
    leadDropPopupUiVariant,
  } = useSelector((state: RootState) => state.leaddrop);

  const boundCourseSelectValue = useMemo(() => {
    const raw = sectionData?.bound_course_id;
    if (raw == null || raw === "" || raw === 0 || raw === "0") return null;
    const idStr = String(raw).trim();
    if (!idStr || idStr === "undefined") return null;
    const list = (courses ?? []) as ICourse[];
    const match = list.find((c) => String(c.id ?? "") === idStr);
    if (!match) {
      return idStr;
    }
    const erp = String(match.erp_unicode ?? "").trim();
    return erp || String(match.id ?? idStr);
  }, [sectionData?.bound_course_id, courses]);

  const lockEnrollmentCourse = Boolean(boundCourseSelectValue);

  const lead7WidgetState = useLeaddrop7WidgetState(
    undefined,
    boundCourseSelectValue,
  );

  const {
    status = true,
    headline_primary = "A full-stack AI engineering program",
    headline_accent = "to ship real-world AI systems",
    body_paragraph_1 = DEFAULT_P1,
    body_paragraph_2 = DEFAULT_P2,
    body_paragraph_3 = DEFAULT_P3,
    stat_1_value = "",
    stat_1_label = "",
    stat_2_value = "",
    stat_2_label = "",
  } = sectionData || {};

  const stats = [
    { value: stat_1_value, label: stat_1_label },
    { value: stat_2_value, label: stat_2_label },
  ].filter(({ value, label }) => safeTrim(value) || safeTrim(label));

  const enableLeadPopup =
    sectionData?.enable_lead_popup === undefined ||
    sectionData?.enable_lead_popup === null
      ? true
      : Boolean(sectionData.enable_lead_popup);

  /**
   * Opt in to the page's INLINE lead form — whatever Page Settings pins, else the
   * university default — in the DESKTOP slot, in place of the Apply CTA / embedded
   * popup card. Off or NULL is today's behaviour, which is why the default is false.
   *
   * Desktop only by design: below 1024px this widget stacks, and a full form at the
   * bottom of a stacked section converts worse than the button that opens the popup.
   * So mobile keeps the Apply Now CTA whichever way this is set.
   *
   * `isCmsShowFlagOn` rather than `Boolean`: a TINYINT(1) reaches here as 0/1,
   * "0"/"1" or a boolean depending on the driver, and `Boolean("0")` is true.
   */
  const enableLeadForm = isCmsShowFlagOn(sectionData?.enable_lead_form, false);

  /**
   * The lead column shows for EITHER switch. The inline form is not "a kind of
   * popup", so requiring `enable_lead_popup` as well would make an author who
   * turned the popup off and the form on get nothing at all — the one outcome
   * neither setting asks for.
   */
  const showLeadColumn = enableLeadForm || enableLeadPopup;

  const embedLeadPopup7 =
    !enableLeadForm &&
    enableLeadPopup &&
    leadDropPopupUiVariant === LEAD_DROP_POPUP_VARIANTS.LEAD_DROP_POPUP_7 &&
    formData;

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current && status) {
  //     FireWidgetViewedEvent(ref, "enrollments_2");
  //   }
  // }, [status]);

  // Register this page's program-enrollment binding so every popup-open path
  // (timer / exit-intent / any CTA), not just this widget's "Apply" button,
  // locks the lead form to the bound course. Cleared on unmount / navigation.
  useEffect(() => {
    dispatch(
      setEnrollmentPageBinding({
        course: boundCourseSelectValue,
        lock: lockEnrollmentCourse,
      }),
    );
    return () => {
      dispatch(setEnrollmentPageBinding({ course: null, lock: false }));
    };
  }, [dispatch, boundCourseSelectValue, lockEnrollmentCourse]);

  if (!status) return null;

  const openLeadPopup = () => {
    dispatch(
      handleOpenLeaddrop({
        variant: LEAD_DROP_VARIANTS.REGISTER,
        ...(boundCourseSelectValue
          ? { course: boundCourseSelectValue, lockEnrollmentCourse }
          : {}),
      }),
    );
  };

  return (
    <section
      ref={ref}
      data-enrollments="2"
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "flex w-full items-center justify-center",
      )}
      style={{
        backgroundColor: SECTION_BG,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div
        className={cn(
          "mx-auto flex h-fit w-full min-w-0 max-w-screen-container flex-col-reverse gap-8 px-6 py-[22px]",
          "min-[1024px]:flex-row-reverse min-[1024px]:items-center min-[1024px]:gap-[74px] min-[1024px]:px-15 min-[1024px]:py-10",
        )}
      >
        {showLeadColumn ? (
          <div
            className={cn(
              "flex w-full justify-center",
              // The two options do NOT share a desktop width. The enrollment card is
              // exactly 392px; the inline form brings its own (form 10 is 400px, form
              // 2001 is 480px at 1430+), so the column hugs it and hands the leftover
              // width back to the `flex-1` copy column instead of leaving dead space.
              enableLeadForm
                ? "min-[1024px]:w-fit min-[1024px]:max-w-[480px] min-[1024px]:shrink-0 min-[1024px]:justify-end"
                : "min-[1024px]:w-[392px] min-[1024px]:shrink-0 min-[1024px]:justify-end",
            )}
          >
            {enableLeadForm ? (
              <>
                <MobileApplyCta onClick={openLeadPopup} />
                {/* Desktop only. `InlineLeadForm` is mounted unconditionally inside
                    this wrapper on purpose: its children are `ssr: false`, which wraps
                    them in a Suspense boundary the server writes markers for, so gating
                    the mount on the store-driven variant would make the boundary exist
                    only on the client — a hydration mismatch even when it renders
                    nothing. The variant lookup lives inside it. `:empty` holds the
                    card's width until its chunk lands so the copy column never
                    re-wraps (higher specificity than `w-fit`, so order is irrelevant). */}
                <div className="hidden min-w-0 min-[1024px]:block min-[1024px]:empty:w-[392px]">
                  <InlineLeadForm />
                </div>
              </>
            ) : embedLeadPopup7 ? (
              <>
                <MobileApplyCta onClick={openLeadPopup} />
                <div
                  className={cn(
                    "hidden min-h-0 min-w-0 min-[1024px]:flex",
                    enrollmentLeadCardClass,
                  )}
                >
                  <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-[26px]">
                    <Leaddrop7
                      embedded
                      open
                      formValues={lead7WidgetState.formValues}
                      setFormValues={lead7WidgetState.setFormValues}
                      step={lead7WidgetState.step}
                      setStep={lead7WidgetState.setStep}
                      showFields={lead7WidgetState.showFields}
                      setShowFields={lead7WidgetState.setShowFields}
                      termsAccepted={lead7WidgetState.termsAccepted}
                      setTermsAccepted={lead7WidgetState.setTermsAccepted}
                      pendingRegistrationAfterOtp={
                        lead7WidgetState.pendingRegistrationAfterOtp
                      }
                      setPendingRegistrationAfterOtp={
                        lead7WidgetState.setPendingRegistrationAfterOtp
                      }
                      data={{
                        formData: formData as unknown as ILeadDropPopup,
                        courses: courses ?? [],
                        defaultValues,
                      }}
                      variant={LEAD_DROP_VARIANTS.REGISTER}
                      enrollmentBoundCourseSelectValue={boundCourseSelectValue}
                      lockEnrollmentCourse={lockEnrollmentCourse}
                    />
                  </div>
                </div>
              </>
            ) : (
              <div
                className={cn(
                  "flex",
                  enrollmentLeadCardClass,
                  "min-h-[320px] min-[1024px]:min-h-[420px]",
                )}
              >
                <div className="flex min-h-[320px] flex-1 flex-col items-center justify-center min-[1024px]:min-h-[420px]">
                  <Button
                    type="button"
                    className={cn("w-full max-w-[280px]", CTA_CLASS)}
                    classType="large"
                    withArrow
                    arrowIcon={applyNowArrowIcon}
                    onClick={openLeadPopup}
                    aria-haspopup="dialog"
                  >
                    Apply Now
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : null}

        <div className="flex flex-1 flex-col items-start gap-6">
          <div className="flex flex-col gap-3">
            <h2 className="text-[28px] font-semibold leading-snug tracking-tight text-[#0A0A0A]">
              <span>{safeTrim(headline_primary)}</span>{" "}
              <span style={{ color: HEADLINE_ACCENT }}>
                {safeTrim(headline_accent)}
              </span>
            </h2>
            <div
              className="flex flex-col gap-4 whitespace-pre-line text-[16px] leading-relaxed"
              style={{ color: BODY_TEXT }}
            >
              {body_paragraph_1 ? <p>{body_paragraph_1}</p> : null}
              {body_paragraph_2 ? <p>{body_paragraph_2}</p> : null}
              {body_paragraph_3 ? <p>{body_paragraph_3}</p> : null}
            </div>
          </div>
          {stats.length ? (
            <div className="flex flex-col gap-8 pt-2">
              {stats.map(({ value, label }, i) => (
                <div key={i} className="flex flex-col gap-1">
                  {safeTrim(value) ? (
                    <span
                      className="text-[14px] leading-tight"
                      style={{ color: BODY_TEXT }}
                    >
                      {value}
                    </span>
                  ) : null}
                  {safeTrim(label) ? (
                    <span
                      className="text-[16px] font-bold leading-tight"
                      style={{ color: HEADLINE_ACCENT }}
                    >
                      {label}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default Enrollments2;
